// JSX port of the companion's renderLearningPath + renderContentCardHtml. READ-ONLY:
// the spark opens a detail bottom-sheet then navigates same-window with ?tid (which
// COMPLETES the patient's content task). Here a content card links straight to the
// article in a NEW tab, rel="noopener noreferrer", WITHOUT ?tid (never completes the
// task, no withings_user_id referer leak), through sanitizeUrl. Cards with no safe
// URL render display-only. The library.html CTA/comeback links (spark-relative, not
// reachable from the back-office) are dropped.

import React from 'react';
import {
  sanitizeUrl, safeImageUrl, filterVisibleContentTasks, withoutShareInvite,
} from '@/logic/patientHome';
import { Icon, ClockOutline } from './icons';

function ctxValue(task, key) {
  const raw = task.context_json;
  let ctx;
  if (typeof raw === 'string') {
    try { ctx = JSON.parse(raw); } catch { ctx = {}; }
  } else ctx = raw || {};
  return ctx[key] || null;
}

const TRACK_LABELS = {
  onboarding: 'Welcome', hypertension: 'Hypertension', 'diabetes-care': 'Diabetes',
  'weight-management': 'Weight', medication: 'Medication', 'lifestyle-spine': 'Lifestyle',
  'journey-milestones': 'Milestone', 'heart-health': 'Heart', 'sleep-health': 'Sleep',
  'device-mastery': 'Device', motivation: 'Mindset',
};

function labelForTrack(track, variant) {
  if (variant === 'challenge') return 'Challenge';
  if (variant === 'tool') return 'Toolkit';
  return TRACK_LABELS[track] || String(track || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ContentCard({ task, variant }) {
  const isDone = task.status === 'completed';
  const track = ctxValue(task, 'track') || task.clinical_domain || 'education';
  const trackLabel = labelForTrack(track, variant);
  const trackClass = variant === 'challenge' ? 'challenge'
    : variant === 'tool' ? 'tool'
      : (String(track).replace(/[^a-z-]/gi, '') || 'motivation');
  const readMinutes = ctxValue(task, 'read_time_minutes') || 3;
  const sourceOrg = ctxValue(task, 'source_org');
  const safeImg = safeImageUrl(ctxValue(task, 'preview_image_url'));
  // NO ?tid — a coordinator preview must never complete the patient's task.
  const href = isDone ? '' : sanitizeUrl(ctxValue(task, 'url'));

  const inner = (
    <>
      <div
        className={`acc-content-card__image acc-content-card__image--${trackClass}`}
        style={safeImg ? { backgroundImage: `url('${safeImg}')` } : undefined}
      >
        <span className={`acc-content-card__track acc-content-card__track--${trackClass}`}>{trackLabel}</span>
        {isDone && (
          <span className="acc-content-card__read-badge" aria-label="Already read"><Icon name="check" size={13} /></span>
        )}
      </div>
      <div className="acc-content-card__body">
        <span className="acc-content-card__title">{task.title}</span>
        <span className="acc-content-card__meta">
          {isDone ? (
            <><Icon name="check" size={12} style={{ verticalAlign: -1, marginRight: 2 }} /> Completed</>
          ) : (
            <>
              <ClockOutline size={12} style={{ opacity: 0.6 }} /> {readMinutes} min {variant === 'challenge' ? 'challenge' : 'read'}
              {sourceOrg ? ` · ${sourceOrg}` : ''}
            </>
          )}
        </span>
      </div>
    </>
  );

  const cls = `acc-content-card acc-content-card--${variant}${isDone ? ' acc-content-card--completed' : ''}`;
  if (href) {
    return (
      <a className={cls} href={href} target="_blank" rel="noopener noreferrer">{inner}</a>
    );
  }
  return <div className={cls}>{inner}</div>;
}

export default function LearningPath({ tasks }) {
  // The share invite looks like content (url, format) but must never become a link in the frame.
  const contentTasks = filterVisibleContentTasks(withoutShareInvite(tasks));

  const toolTasks = contentTasks.filter((t) => ctxValue(t, 'format') === 'interactive-tool');
  const feedTasks = contentTasks.filter((t) => ctxValue(t, 'format') !== 'interactive-tool');
  const completedCount = feedTasks.filter((t) => t.status === 'completed').length;
  const total = feedTasks.length;

  const pending = feedTasks.filter((t) => t.status !== 'completed');
  const done = feedTasks.filter((t) => t.status === 'completed')
    .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
  const sorted = [...pending, ...done];

  return (
    <section className="acc-section fade-up" aria-labelledby="ph-learn-title">
      <div className="acc-section__header">
        <h2 className="acc-section__title" id="ph-learn-title">
          Your Learning Path
          {total > 0 && <span className="acc-section__badge acc-section__badge--count">{completedCount}/{total}</span>}
        </h2>
      </div>
      <div>
        {contentTasks.length === 0 ? (
          <p className="acc-empty-note acc-empty-note--bare">No content assigned yet</p>
        ) : (
          <>
            {toolTasks.length > 0 && (
              <div className="acc-toolkit">
                <div className="acc-toolkit__label">Your toolkit</div>
                <div className="acc-toolkit__cards">
                  {toolTasks.map((t) => <ContentCard key={t.task_id} task={t} variant="tool" />)}
                </div>
              </div>
            )}
            <div className="acc-carousel">
              {sorted.map((t) => {
                const fmt = ctxValue(t, 'format');
                const variant = fmt === 'challenge' ? 'challenge' : 'article';
                return <ContentCard key={t.task_id} task={t} variant={variant} />;
              })}
              {pending.length === 0 && (
                <div className="acc-content-card acc-content-card--article acc-content-card--comeback">
                  <div className="acc-content-card__image"><Icon name="sparkle" size={40} /></div>
                  <div className="acc-content-card__body">
                    <span className="acc-content-card__title">All caught up!</span>
                    <p className="acc-content-card__teaser">New content unlocks as the journey progresses.</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
